import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";

const USER_PROFILE_CACHE_KEY = "user_profile_cache";
const USER_PROFILE_CACHE_MAX_AGE_MS = 1000 * 60 * 30; // 30 min cache time until it will be invalidated

function loadCache() {
    try {
        return JSON.parse(sessionStorage.getItem(USER_PROFILE_CACHE_KEY) || "null");
    } catch {
        return null;
    }
}

function saveCache(data) {
    sessionStorage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify(data));
}

export function cleanUserProfileCache() {
    sessionStorage.removeItem(USER_PROFILE_CACHE_KEY);
}

export function useUserProfile() {
    const { accounts, instance } = useMsal();
    const account = accounts[0] || null;

    const [profile, setProfile] = useState(null);
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadProfile = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Retrieve access token
            const result = await instance.acquireTokenSilent({
                scopes: ["User.Read"],
                account
            });

            const token = result.accessToken;

            // Retrieve user profile from ms graph/me api
            const res = await axios.get(
                "https://graph.microsoft.com/v1.0/me", {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Retrieve user photo ---
            var profilePhotoUrl = undefined;
            try {
                const photoResponse = await axios.get(
                    "https://graph.microsoft.com/v1.0/me/photo/$value", {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                    validateStatus: () => true // do not throw automatically
                });

                if (photoResponse.status === 200) {
                    // Generate blob URL for displaying in <img />
                    const blobToBase64 = (blob) => {
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                    }
                    profilePhotoUrl = await blobToBase64(photoResponse.data);
                }
            } catch {
            }

            // Server response timestamp (for cache invalidate)
            const serverTime = new Date(res.headers.date || Date.now()).getTime();

            // Save to cache
            saveCache({
                profile: res.data,
                profilePhotoUrl: profilePhotoUrl,
                fetchedAt: serverTime
            });

            setProfile(res.data);
            setProfilePhotoUrl(profilePhotoUrl);
        } catch (err) {
            setError(err);
            // console.error(`UserProfile retrieve error, errorCode: ${err.errorCode}, errorMessage: ${err.errorMessage}`);
        } finally {
            setLoading(false);
        }
    }, [account]);

    useEffect(() => {
        const cache = loadCache();

        if (cache) {
            const age = Date.now() - cache.fetchedAt;

            // Check cache invalidation time
            if (age < USER_PROFILE_CACHE_MAX_AGE_MS) {
                setProfile(cache.profile);
                setProfilePhotoUrl(cache.profilePhotoUrl);                
                setLoading(false);
                return;
            }
        }

        // Refresh user profile
        loadProfile();
    }, [loadProfile]);

    return {
        // MSAL user information
        name: account?.idTokenClaims?.name,
        login: account?.idTokenClaims?.preferred_username,
        roles: account?.idTokenClaims?.roles || [],
        // MS-Graph profile
        profile: {
            id: profile?.id,
            displayName: profile?.displayName,
            givenName: profile?.givenName,
            surname: profile?.surname,
            email: profile?.mail,
            officeLocation: profile?.officeLocation,
            jobTitle: profile?.jobTitle,
            mobilePhone: profile?.mobilePhone,
            businessPhones: profile?.businessPhones,
            photoUrl: profilePhotoUrl,
        },
        loading,
        error,
        refresh: loadProfile
    };
}
